import {
  dispatchAction,
  ObservableState,
  persistentReducedStream,
  reducedStream,
  reducer,
  streamReducer,
} from '@ardoq/rxbeach';
import {
  ChangedSettings,
  PersistedViewpointModeSettings,
  ViewpointModeInstanceViewSettings,
  ViewpointModeViewKey,
  ViewpointModeViewSettingsShape,
  ViewpointModeViewSettingsShapeWithoutSymbols,
  ViewpointModeViewSettingsWithChanges,
} from './types';
import { SYMBOL_TRANSACTION_ID, ViewSettingsContexts } from '@ardoq/api-types';
import { DEFAULT_VIEWPOINT_MODE_SETTINGS } from './defaultStreamState';
import { isEqual, pick, cloneDeep } from 'lodash';
import { composeNewSettings, extractChangedSettings } from 'viewSettings/utils';
import {
  saveViewSettingsChange,
  viewpointModeViewSettingsUpdateRequested,
  ViewpointModeViewSettingsUpdateRequestedPayload,
} from './actions';
import {
  isViewSettingsUpdateRelevantForInstance,
  mergeViewSettings,
  sanitizeSingleViewSettings,
  typedEntries,
} from './utils';
import { getSavedSettings$ } from './savedSettings$';
import { DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE } from './defaultPersistedSettings';
import {
  distinctUntilChanged,
  filter,
  map,
  Observable,
  shareReplay,
  tap,
} from 'rxjs';

/**
 * Set the saved settings value if the state property equals to the default settings, otherwise
 *  do not override the current settings, they presumably contain the last value.
 */
const applySavedSettings = (
  state: ViewpointModeViewSettingsWithChanges,
  savedSettings: PersistedViewpointModeSettings
): ViewpointModeViewSettingsWithChanges => {
  const currentSettings = state.currentSettings;

  const viewsToUpdate = typedEntries(currentSettings)
    .filter(([key, value]) =>
      isEqual(value, DEFAULT_VIEWPOINT_MODE_SETTINGS[key])
    )
    .map(([key]) => key);

  // these are the views that have default view settings and should be updated with saved settings
  const relevantCurrentSettings: Partial<ViewpointModeViewSettingsShape> = pick(
    cloneDeep(currentSettings),
    viewsToUpdate
  );

  // saved settings that correspond to the views that should be updated
  const relevantSavedSettings: Partial<PersistedViewpointModeSettings> = pick(
    cloneDeep(savedSettings),
    viewsToUpdate
  );

  // Pick only creates a shallow copy, _.mergeWith then mutates it.
  // Therefore we clone the arguments
  const mergedSettings: Partial<ViewpointModeViewSettingsShape> =
    mergeViewSettings(relevantCurrentSettings, relevantSavedSettings);

  return {
    currentSettings: {
      ...state.currentSettings,
      ...mergedSettings,
    },
    changedSettings: [],
    persistent: false,
  };
};

const updateViewSettings = (
  state: ViewpointModeViewSettingsWithChanges,
  {
    viewId,
    viewInstanceId,
    settings: { [SYMBOL_TRANSACTION_ID]: transactionId, ...settings },
    persistent,
  }: ViewpointModeViewSettingsUpdateRequestedPayload
): ViewpointModeViewSettingsWithChanges => {
  const currentViewSettings = state.currentSettings[viewId];

  const nextSettings = sanitizeSingleViewSettings(
    composeNewSettings(currentViewSettings, settings),
    DEFAULT_VIEWPOINT_MODE_SETTINGS[viewId]
  );

  const changedSettings = extractChangedSettings({
    nextSettings,
    settings,
    // Typing partial object entries will result in a type assertion.
    // While this assertion should come from the function, the function
    // is currently shared with legacy code, so we place it here.
  }) as ChangedSettings<ViewpointModeViewSettingsShapeWithoutSymbols>;

  return {
    currentSettings: {
      ...state.currentSettings,
      [viewId]: { ...nextSettings, [SYMBOL_TRANSACTION_ID]: transactionId },
    },
    changedSettings,
    persistent,
    viewInstanceId,
    viewId,
  };
};

const savedSettings$ = getSavedSettings$(
  ViewSettingsContexts.VIEWPOINT_MODE,
  DEFAULT_PERSISTED_SETTINGS_IN_VIEWPOINT_MODE
);

const initialState: ViewpointModeViewSettingsWithChanges = {
  currentSettings: cloneDeep(DEFAULT_VIEWPOINT_MODE_SETTINGS),
  changedSettings: [],
  persistent: false,
};

/**
 * This is the 'global' view settings state for the respective environment. It holds
 * the initial state for all private streams created through new subscriptions.
 * (one for all views)
 */
const _viewpointModeSettingsState$: ObservableState<ViewpointModeViewSettingsWithChanges> =
  persistentReducedStream<ViewpointModeViewSettingsWithChanges>(
    `contextViewSettings-${ViewSettingsContexts.VIEWPOINT_MODE}`,
    initialState,
    [
      reducer(viewpointModeViewSettingsUpdateRequested, updateViewSettings),
      streamReducer(savedSettings$, applySavedSettings),
    ]
  );

/**
 * This is the observable emitting updates to all subscribed private streams. It's main
 * purpose is to facilitate the persistence of changes to the backend.
 * (one stream for all views, but the persisted changes are for a specific view type)
 */
export const viewpointModeSettingsForAllViews$: Observable<ViewpointModeViewSettingsWithChanges> =
  _viewpointModeSettingsState$.pipe(
    distinctUntilChanged((a, b) => isEqual(a, b)),
    tap(({ currentSettings, changedSettings, persistent, viewId }) => {
      if (!persistent || !viewId || !changedSettings.length) {
        return;
      }

      const modifiedViewSettings = currentSettings[viewId];

      dispatchAction(
        saveViewSettingsChange({
          contextId: ViewSettingsContexts.VIEWPOINT_MODE,
          viewId,
          changedProperties: changedSettings,
          modifiedViewSettings: modifiedViewSettings,
        })
      );
    }),
    shareReplay({ refCount: true, bufferSize: 1 })
  );

const getPrivateStreamUpdatesReducer =
  <T extends ViewpointModeViewKey>(viewId: T) =>
  (
    state: ViewpointModeInstanceViewSettings<T>,
    {
      currentSettings,
      changedSettings,
      persistent,
    }: ViewpointModeViewSettingsWithChanges
  ): ViewpointModeInstanceViewSettings<T> => ({
    ...state,
    currentSettings: currentSettings[viewId],
    // At this point, thanks to the filter, we know `settings.viewId` is `T`.
    // This means `currentSettings[subscriberViewId]` is correctly typed as `ViewpointModeViewSettingsShape[T]`.
    // However, `changedSettings` is still `ChangedSettings<ViewpointModeViewSettingsShape>`, which is a
    // broader type. To avoid runtime narrowing, we assert the more specific type for it to be accepted by the
    // downstream logic.
    changedSettings: changedSettings as [
      keyof ViewpointModeViewSettingsShape[T],
      any,
    ][],
    persistent,
  });

/**
 * The streams below are 'private', they get the initial state from the global state stream,
 * but the updates are filtered to only include updates relevant for the specific view instance.
 * This is to avoid all view instances receiving all updates, which would lead to unnecessary
 * re-renders. It also facilitates different instances of the same view type having different
 * settings during their lifecycle.
 * (one stream per view instance)
 */
export const getViewpointModeViewSettingsWithChanges$ = <
  T extends ViewpointModeViewKey,
>(
  subscriberViewId: T,
  subscriberViewInstanceId: string
): Observable<ViewpointModeInstanceViewSettings<T>> => {
  const initialState$ = _viewpointModeSettingsState$.pipe(
    map(({ currentSettings }): ViewpointModeInstanceViewSettings<T> => ({
      currentSettings: currentSettings[subscriberViewId],
      changedSettings: [],
      persistent: false,
      viewId: subscriberViewId,
      viewInstanceId: subscriberViewInstanceId,
    }))
  );

  const privateStreamUpdates$ = viewpointModeSettingsForAllViews$.pipe(
    filter(({ viewId: updatedViewId, viewInstanceId: updatedViewInstanceId }) =>
      isViewSettingsUpdateRelevantForInstance({
        subscriberViewId,
        subscriberViewInstanceId,
        updatedViewId,
        updatedViewInstanceId,
      })
    )
  );

  return reducedStream(
    `viewpoint-mode-settings-with-changes-${subscriberViewInstanceId}`,
    initialState$,
    [
      streamReducer(
        privateStreamUpdates$,
        getPrivateStreamUpdatesReducer(subscriberViewId)
      ),
    ]
  );
};

export const getViewpointModeViewSettings$ = <T extends ViewpointModeViewKey>(
  subscriberViewId: T,
  subscriberViewInstanceId: string
) =>
  getViewpointModeViewSettingsWithChanges$(
    subscriberViewId,
    subscriberViewInstanceId
  ).pipe(map(({ currentSettings }) => currentSettings));
